import {
  HStack,
  SkeletonCircle,
  VStack,
  Box,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VisibleIf } from 'Atoms';
import { UserAvatar } from 'Organisms/UserAvatar';
import { Menu } from 'Molecules/Menu';
import { LanguageEditor } from 'Organisms/LanguageEditor';
import { DarkModeSwitch } from 'Atoms/DarkModeSwitch';
import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Typography } from 'Tokens';
import { ChevronUpDownIcon } from 'Tokens/Icons/Direction';
import { WorldIcon } from 'Tokens/Icons/Data';
import { SignOutIcon } from 'Tokens/Icons/Function';
import { SettingsIcon } from 'Tokens/Icons/Function';
import { hasSalesOrSupportRole } from 'utils/users';
import { useAuth } from 'Providers/auth/AuthProvider';

export const ProfileSidebar = ({
  isOpen,
  isScreenLarge,
  isMenuOpen,
  setIsMenuOpen,
}: {
  isOpen: boolean;
  isScreenLarge: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const {user,signOut} = useAuth();
  const { t } = useTranslation('common');
  const client = useApolloClient();
  const navigate = useNavigate();
  const {
    isOpen: isLanguageEditorOpen,
    onOpen: onLanguageEditorOpen,
    onClose: onLanguageEditorClose,
  } = useDisclosure();

  useOutsideClick({
    ref: menuRef,
    handler: () => {
      if(!isMenuOpen) return
      setIsMenuOpen(false);
    },
    enabled:isMenuOpen
  });
  const handleSettingsClick = () => {
    navigate('settings/company-details');
  };

  const handleSignOut = () => {
    client.stop();
    signOut();
  };

  useEffect(() => {
    if (!isOpen && !isScreenLarge) {
      setIsMenuOpen(false);
    }
  }, [isOpen, isScreenLarge]);

  return (
    <Box
      width="100%"
      _hover={{
        bg: 'bg.onAccent.hover',
      }}
      borderRadius="8px"
      _pressed={{ bg: 'bg.pressed' }}
      ref={menuRef}
    >
      <Menu
        usePortal
        isOpen={!isScreenLarge ? isOpen && isMenuOpen : isMenuOpen}
        onMenuButtonClick={() => setIsMenuOpen(!isMenuOpen)}
        menuButton={
          <HStack
            spacing="32px"
            textAlign="center"
            height="48px"
            borderRadius="8px"
            cursor="pointer"
            padding="8px 20px 8px 6px"
          >
            <HStack spacing="8px" padding="0px" width="148px">
              {user ? (
                <SkeletonCircle isLoaded={user != null} boxSize="32px">
                  <UserAvatar size="sm" user={user ?? {}} />
                </SkeletonCircle>
              ) : undefined}
              <VisibleIf condition={isOpen}>
                <VStack
                  spacing="0px"
                  transition="all 0.4s"
                  opacity={isOpen ? 1 : 0}
                  alignItems="start"
                  width="108px"
                >
                  <Typography
                    variant="bodyStrong"
                    color="text.sidebar"
                    noOfLines={1}
                    textAlign="start"
                  >
                    {user?.displayName}
                  </Typography>
                  <Typography
                    variant="detail"
                    color="text.sidebar"
                    opacity="0.8"
                    noOfLines={1}
                    textAlign="start"
                  >
                    Your account
                  </Typography>
                </VStack>
              </VisibleIf>
            </HStack>
            <ChevronUpDownIcon boxSize="16px" color={!isOpen ? 'text.onAccent' : ''} />
          </HStack>
        }
        sections={[
          {
            actions: [
              {
                id: 'settings',
                title: 'Settings',
                leftElement: <SettingsIcon color="inherit" />,
                onClick: handleSettingsClick,
              },
              {
                id: 'language',
                title: t('common:actions.language'),
                leftElement: <WorldIcon color="inherit" />,
                onClick: onLanguageEditorOpen,
              },
            ],
          },
          ...(hasSalesOrSupportRole(user?.roles)
            ? [
                {
                  actions: [
                    {
                      id: 'dark-mode-switch',
                      reactNode: <DarkModeSwitch key={'dark-mode-switch'} />,
                    },
                  ],
                },
              ]
            : []),
          {
            actions: [
              {
                id: 'logout',
                title: t('common:actions.logOut'),
                leftElement: <SignOutIcon color="inherit" />,
                onClick: handleSignOut,
              },
            ],
          },
        ]}
        size={'md'}
        placement="right"
      />
      {user && (
        <LanguageEditor isOpen={isLanguageEditorOpen} onClose={onLanguageEditorClose} user={user} />
      )}
    </Box>
  );
};
